import { FeaturedContentOverride } from '@atlassian/amkt-addon-discovery';
import { parse as parseAri } from '@atlassian/cs-ari';
import { Option } from 'funfix-core';
import { List, Map, Set } from 'immutable';
import React from 'react';
import BYOTile from '../../components/common/BYOTile';
import { makeErrorTranslation } from '../../records/ErrorTranslation';
import { makeAddonDiscoveryConfig, makeHostConfig, makeTokenConfig } from '../../records/HostConfig';
import { STRIDE_CONTEXT_TYPE } from '../../types/ContextType';
import { parse as parseUrlTemplate } from '../../utils/url-template-utils';
export default makeHostConfig({
  addonDiscovery: makeAddonDiscoveryConfig({
    featuredContentOverride: Option.some(_ref => {
      let {
        openAppSpecificPasswordDialog
      } = _ref;
      return List([new FeaturedContentOverride({
        index: 0,
        // eslint-disable-next-line react/display-name
        render: () => /*#__PURE__*/React.createElement(BYOTile, {
          openAppSpecificPasswordDialog: openAppSpecificPasswordDialog
        })
      })]);
    }),
    productKey: 'stride',
    hiddenFilters: Map({
      sort: true,
      moreFilters: Set(['free'])
    })
  }),
  analytics: {
    productName: 'stride',
    getSubproduct: _ => {
      /* no subproduct */
    }
  },
  contextType: STRIDE_CONTEXT_TYPE,
  errors: Map({
    AddonDefinitionNotFoundError: makeErrorTranslation({
      title: "We couldn't find the app",
      description: "It's possible the link to the app is broken."
    }),
    DescriptorFetchError: makeErrorTranslation({
      title: "We couldn't find the app",
      description: "We couldn't find an app at that installation URL. Please make sure the URL is correct.",
      validationError: true
    }),
    InstallationConflictError: makeErrorTranslation({
      title: "We're seeing double!",
      description: 'The app is already installed in this conversation.'
    }),
    InvalidAddonOriginError: makeErrorTranslation({
      title: "This app isn't registered",
      description: 'Please enter the installation URL from your app listing in developer.atlassian.com',
      validationError: true
    }),
    InvalidDefinitionDetailsQueryError: makeErrorTranslation({
      title: "The URL isn't formatted correctly",
      description: 'Please enter a valid installation URL, beginning with http: or https:',
      validationError: true
    }),
    InvalidDescriptorError: makeErrorTranslation({
      title: "This app isn't working quite right",
      description: 'The app at that installation URL seems to be broken. ' + 'Please reach out to our Support team, so we can get it fixed.',
      showTID: true
    }),
    default: makeErrorTranslation({
      title: 'We ran into a little trouble',
      description: 'It might just be a hiccup. Try again in a bit.',
      showTID: true
    })
  }),
  featureFlags: {
    productName: 'stride'
  },
  installedApps: {
    globallyInstalledApps: {
      sectionHeading: 'Apps installed in all conversations',
      sectionDescription: 'Some apps are installed in all public rooms and conversations.'
    },
    locallyInstalledApps: {
      sectionHeading: 'Apps installed in this conversation'
    }
  },
  name: 'Stride',
  installationLocation: 'conversation',
  isCloseable: true,
  removeFromSite: {
    actionSummary: appName => "Remove ".concat(appName, " from all conversations"),
    confirmation: appName => "You're about to remove ".concat(appName, " from multiple conversations.             Participants in these conversations won't be able to use ").concat(appName, " in Stride. This can't be undone."),
    successNotification: appName => "".concat(appName, " was removed from all conversations")
  },
  token: makeTokenConfig({
    exampleBody: {
      body: {
        version: 1,
        type: 'doc',
        content: [{
          type: 'paragraph',
          content: [{
            type: 'text',
            text: 'My first message!'
          }]
        }]
      }
    },
    exampleEndpoint: context => {
      const ari = parseAri(context.id);
      return parseUrlTemplate('https://api.atlassian.com/site/{cloudId}/conversation/{conversationId}/message').expand({
        cloudId: ari.cloudId,
        conversationId: ari.resourceId
      });
    },
    explanation: 'API tokens give you a simple way to send messages to a conversation from ' + 'your own code, without having to build an app.',
    endpointVerb: 'post messages'
  }),
  supportsManage: true,
  welcomeHeader: 'Welcome to Stride!',
  welcomeSubheader: 'Pick a conversation to get going...'
});